import { useEffect, useCallback, useState } from 'react';
import { ROW_INDEX, MIN_ROW_INDEX } from '../components/Gradebook/constants';
import { GRADEBOOK_SETTINGS } from '@macmillan-learning/gradebook-shared';

// Column identifiers
const COLUMN_ID = {
  NAME: 'name',
  COURSE_TOTAL: 'courseTotal',
  CATEGORY_TOTAL_PREFIX: 'category-total-',
};

// Mirrors what the category total cell renders (useGradebookColumns):
// weighted percentage, plain percentage, or points when percentages are off.
const formatCategoryTotal = (categoryTotal, { gradingType, showPercent }) => {
  if (!categoryTotal) return 'No grade';
  if (categoryTotal.isUngraded) {
    return `${categoryTotal.numCompleted ?? 0} completed`;
  }

  if (!showPercent) {
    const earned = categoryTotal.studentPoints ?? 0;
    const possible = categoryTotal.assigned_points ?? 0;
    return `${earned} out of ${possible} points`;
  }

  const isWeighted = gradingType === GRADEBOOK_SETTINGS.gradingTypeWeighted;
  const percent = isWeighted
    ? categoryTotal.weightedCategoryTotal
    : categoryTotal.percentage;

  return typeof percent === 'number' ? `${percent.toFixed(1)}%` : 'No grade';
};

// Layout constants for scroll calculations
const LAYOUT = {
  STICKY_NAME_COLUMN_WIDTH: 250,
  STICKY_HEADER_HEIGHT: 150,
  SCROLL_PADDING: 20,
};

// Selector for interactive elements within a cell.
// Includes elements with tabIndex={-1} since they are focusable
// programmatically when entering "inside cell" mode via Tab.
// Excludes SVGs because MLIcon sets tabIndex={-1} by default on all icons.
const FOCUSABLE_SELECTOR =
  'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not(svg)';

// Helper to build cell selector
const getCellSelector = (rowIndex, columnId) =>
  `[data-row-index="${rowIndex}"][data-column-id="${columnId}"]`;

export function useKeyboardNavigation({
  tableRows,
  columns,
  onCellFocus,
  onCellEdit,
  onCellActivate,
  onAnnouncement,
  data,
  scrollToRow,
  gradingType,
  showPercent,
}) {
  const [focusedCell, setFocusedCell] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  // Track when user is navigating inside a cell (Tab cycles through nested elements)
  const [isInsideCell, setIsInsideCell] = useState(false);

  // Get all focusable elements inside a cell
  const getFocusableElements = useCallback((cellElement) => {
    if (!cellElement) return [];
    return Array.from(cellElement.querySelectorAll(FOCUSABLE_SELECTOR));
  }, []);

  // Focus back to the cell itself (exiting "inside cell" mode)
  const focusCell = useCallback((rowIndex, columnId) => {
    const cellElement = document.querySelector(
      getCellSelector(rowIndex, columnId),
    );
    if (cellElement instanceof HTMLElement) {
      cellElement.focus();
      setIsInsideCell(false);
    }
  }, []);

  const ensureCellVisible = useCallback((cellElement) => {
    const scrollContainer = cellElement.closest('[role="region"]');
    if (!scrollContainer) return;

    const cellRect = cellElement.getBoundingClientRect();
    const containerRect = scrollContainer.getBoundingClientRect();

    const { STICKY_NAME_COLUMN_WIDTH, STICKY_HEADER_HEIGHT, SCROLL_PADDING } =
      LAYOUT;

    const visibleLeft =
      containerRect.left +
      (cellElement.dataset.columnId === COLUMN_ID.NAME
        ? 0
        : STICKY_NAME_COLUMN_WIDTH);
    const visibleTop = containerRect.top + STICKY_HEADER_HEIGHT;
    const visibleRight = containerRect.right - SCROLL_PADDING;
    const visibleBottom = containerRect.bottom - SCROLL_PADDING;

    const isFullyVisible =
      cellRect.left >= visibleLeft + SCROLL_PADDING &&
      cellRect.right <= visibleRight &&
      cellRect.top >= visibleTop + SCROLL_PADDING &&
      cellRect.bottom <= visibleBottom;

    if (!isFullyVisible) {
      let scrollLeft = scrollContainer.scrollLeft;
      let scrollTop = scrollContainer.scrollTop;

      if (cellRect.left < visibleLeft + SCROLL_PADDING) {
        scrollLeft -= visibleLeft + SCROLL_PADDING - cellRect.left;
      } else if (cellRect.right > visibleRight) {
        scrollLeft += cellRect.right - visibleRight + SCROLL_PADDING;
      }

      if (cellRect.top < visibleTop + SCROLL_PADDING) {
        scrollTop -= visibleTop + SCROLL_PADDING - cellRect.top;
      } else if (cellRect.bottom > visibleBottom) {
        scrollTop += cellRect.bottom - visibleBottom + SCROLL_PADDING;
      }

      scrollContainer.scrollTo({
        left: scrollLeft,
        top: scrollTop,
        behavior: 'smooth',
      });
    }
  }, []);

  const moveFocus = useCallback(
    (direction) => {
      if (!focusedCell) {
        // Default to first header cell (category row, name column)
        setFocusedCell({ rowIndex: MIN_ROW_INDEX, columnId: columns[0]?.id });
        return;
      }

      const currentColIndex = columns.findIndex(
        (col) => col.id === focusedCell.columnId,
      );
      let newRowIndex = focusedCell.rowIndex;
      let newColIndex = currentColIndex;

      switch (direction) {
        case 'up':
          // Allow navigation up into header rows (negative indices)
          newRowIndex = Math.max(MIN_ROW_INDEX, focusedCell.rowIndex - 1);
          break;
        case 'down':
          newRowIndex = Math.min(
            tableRows.length - 1,
            focusedCell.rowIndex + 1,
          );
          break;
        case 'left':
          newColIndex = Math.max(0, currentColIndex - 1);
          break;
        case 'right':
          newColIndex = Math.min(columns.length - 1, currentColIndex + 1);
          break;
        default:
          break;
      }

      const newPosition = {
        rowIndex: newRowIndex,
        columnId: columns[newColIndex]?.id,
      };

      setFocusedCell(newPosition);
      onCellFocus?.(newPosition);

      // Only scroll virtualizer for data rows (positive indices)
      if (scrollToRow && newRowIndex >= 0) {
        scrollToRow(newRowIndex);
      }

      // Use requestAnimationFrame to wait for virtualizer to render the row
      requestAnimationFrame(() => {
        const cellElement = document.querySelector(
          getCellSelector(newRowIndex, columns[newColIndex]?.id),
        );
        if (cellElement instanceof HTMLElement) {
          cellElement.focus();
          ensureCellVisible(cellElement);

          // Generate announcement based on row type
          if (onAnnouncement) {
            const column = columns[newColIndex];
            let announcement = '';

            if (newRowIndex === ROW_INDEX.CATEGORY) {
              // Category row - announce the category name
              const categoryName =
                column?.meta?.categoryName || column?.id || 'category';
              announcement = `Category row: ${categoryName}`;
            } else if (newRowIndex === ROW_INDEX.ASSIGNMENT) {
              // Assignment row
              announcement = `Assignment header: ${column?.meta?.assignmentName || column?.id || 'column'}`;
            } else if (newRowIndex === ROW_INDEX.AVERAGE) {
              // Average row
              announcement = `Class average row, ${column?.meta?.assignmentName || column?.id || 'column'}`;
            } else if (data) {
              // Data row
              const row = tableRows[newRowIndex]?.original;
              if (row && column) {
                if (column.id === COLUMN_ID.NAME) {
                  announcement = `Student: ${row.student.name}`;
                } else if (
                  column.id?.startsWith(COLUMN_ID.CATEGORY_TOTAL_PREFIX)
                ) {
                  const categoryId = column.id.replace(
                    COLUMN_ID.CATEGORY_TOTAL_PREFIX,
                    '',
                  );
                  // categoryTotals holds objects, not numbers.
                  const totalText = formatCategoryTotal(
                    row.categoryTotals?.[categoryId],
                    { gradingType, showPercent },
                  );
                  announcement = `${row.student.name}, Category Total: ${totalText}`;
                }
              }
            }

            if (announcement) {
              onAnnouncement(announcement);
            }
          }
        }
      });
    },
    [
      focusedCell,
      tableRows,
      columns,
      onCellFocus,
      ensureCellVisible,
      onAnnouncement,
      data,
      scrollToRow,
      gradingType,
      showPercent,
    ],
  );

  const handleKeyDown = useCallback(
    (e) => {
      // Only skip when focus is inside a modal. A document-wide [aria-modal="true"]
      // query breaks when CMPs (e.g. OneTrust) leave a preference dialog in the DOM
      // while focus remains in the gradebook.
      const activeEl = document.activeElement;
      if (activeEl?.closest('[aria-modal="true"]')) {
        return;
      }

      // Only handle keyboard navigation when focus is within the gradebook grid
      // This allows normal keyboard behavior for header buttons (Enter, Space, Tab, etc.)
      const gradebookGrid = document.querySelector(
        '[role="grid"][aria-label="Student grades"]',
      );
      const scrollContainer = gradebookGrid?.closest('[role="region"]');
      const isFocusInGradebook =
        scrollContainer?.contains(document.activeElement) ||
        document.activeElement === scrollContainer;

      // Only intercept keys when focus is in the gradebook
      // This ensures buttons in the header work normally with Enter/Space
      if (!isFocusInGradebook) {
        return; // Let default keyboard behavior happen for elements outside the table
      }

      // For Tab key, try to use focusedCell or derive from activeElement
      let effectiveFocusedCell = focusedCell;
      if (e.key === 'Tab' && !effectiveFocusedCell) {
        // Try to derive cell info from the currently focused element
        const activeEl = document.activeElement;
        if (
          activeEl instanceof HTMLElement &&
          activeEl.dataset.rowIndex !== undefined
        ) {
          effectiveFocusedCell = {
            rowIndex: parseInt(activeEl.dataset.rowIndex, 10),
            columnId: activeEl.dataset.columnId,
          };
          // Also update the state so subsequent navigation works
          setFocusedCell(effectiveFocusedCell);
        } else {
          return; // Let default Tab behavior happen
        }
      }

      if (isEditing) {
        if (e.key === 'Escape') {
          setIsEditing(false);
          e.preventDefault();
        } else if (e.key === 'Enter' && !e.shiftKey) {
          setIsEditing(false);
          moveFocus('down');
          e.preventDefault();
        }
        return;
      }

      // Handle "inside cell" mode - user is navigating nested elements
      if (isInsideCell && focusedCell) {
        if (e.key === 'Escape') {
          // Escape exits "inside cell" mode and returns focus to the cell
          focusCell(focusedCell.rowIndex, focusedCell.columnId);
          e.preventDefault();
          return;
        }

        // In inside-cell mode, let the focused element's own handler
        // process Enter/Space (e.g., sort button, copy email, name link)
        if (e.key === 'Enter' || e.key === ' ') {
          return;
        }

        if (e.key === 'Tab') {
          // Tab cycles through focusable elements inside the cell
          const cellElement = document.querySelector(
            getCellSelector(focusedCell.rowIndex, focusedCell.columnId),
          );
          const focusableElements = getFocusableElements(cellElement);

          if (focusableElements.length > 0) {
            const currentIndex = focusableElements.indexOf(
              document.activeElement,
            );

            if (e.shiftKey) {
              // Shift+Tab: go backwards
              if (currentIndex <= 0) {
                // At first element, exit "inside cell" mode and go to previous cell
                setIsInsideCell(false);
                // Fall through to normal Tab handling below
              } else {
                focusableElements[currentIndex - 1].focus();
                e.preventDefault();
                return;
              }
            } else {
              // Tab: go forwards
              if (currentIndex >= focusableElements.length - 1) {
                // At last element, exit "inside cell" mode and go to next cell
                setIsInsideCell(false);
                // Fall through to normal Tab handling below
              } else {
                focusableElements[currentIndex + 1].focus();
                e.preventDefault();
                return;
              }
            }
          }
        }

        // Arrow keys exit "inside cell" mode and navigate the grid
        if (
          ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)
        ) {
          setIsInsideCell(false);
          // Continue to normal arrow key handling below
        }
      }

      switch (e.key) {
        case 'ArrowUp':
          moveFocus('up');
          e.preventDefault();
          break;
        case 'ArrowDown':
          moveFocus('down');
          e.preventDefault();
          break;
        case 'ArrowLeft':
          moveFocus('left');
          e.preventDefault();
          break;
        case 'ArrowRight':
          moveFocus('right');
          e.preventDefault();
          break;
        case 'Tab': {
          // Use effectiveFocusedCell which may have been derived from activeElement
          const cellForTab = effectiveFocusedCell || focusedCell;
          if (!cellForTab) break;

          const currentColIndex = columns.findIndex(
            (col) => col.id === cellForTab.columnId,
          );

          // Check if the current cell has focusable elements inside
          const currentCellElement = document.querySelector(
            getCellSelector(cellForTab.rowIndex, cellForTab.columnId),
          );
          const focusableElements = getFocusableElements(currentCellElement);

          // For cells with MULTIPLE focusable elements, enter "inside cell" mode
          // For cells with exactly ONE element, skip inside cell mode - Tab goes
          // directly to the element, then next Tab moves to next cell
          if (focusableElements.length > 1 && !isInsideCell) {
            const targetElement = e.shiftKey
              ? focusableElements[focusableElements.length - 1]
              : focusableElements[0];

            if (targetElement && targetElement instanceof HTMLElement) {
              // Prevent default Tab FIRST to stop browser from moving focus
              e.preventDefault();
              e.stopPropagation();

              // Capture values before async - event object may not be reliable in rAF
              const goToLast = e.shiftKey;
              const rowIndex = cellForTab.rowIndex;
              const columnId = cellForTab.columnId;

              // Update state first, then focus after React re-render
              // We must re-query the element in rAF because React may replace the DOM node
              setIsInsideCell(true);

              requestAnimationFrame(() => {
                const cellEl = document.querySelector(
                  getCellSelector(rowIndex, columnId),
                );
                const elements = getFocusableElements(cellEl);
                const newTarget = goToLast
                  ? elements[elements.length - 1]
                  : elements[0];
                if (newTarget instanceof HTMLElement) {
                  newTarget.focus();
                }
              });

              break;
            }
          }

          if (e.shiftKey) {
            // Shift+Tab: go backwards
            if (
              currentColIndex === 0 &&
              cellForTab.rowIndex === MIN_ROW_INDEX
            ) {
              // At first cell (top-left of category row) - let Tab leave the table (default behavior)
              setFocusedCell(null);
              return;
            } else if (
              currentColIndex === 0 &&
              cellForTab.rowIndex > MIN_ROW_INDEX
            ) {
              const newPosition = {
                rowIndex: cellForTab.rowIndex - 1,
                columnId: columns[columns.length - 1]?.id,
              };
              setFocusedCell(newPosition);
              onCellFocus?.(newPosition);

              // Only scroll virtualizer for data rows
              if (scrollToRow && newPosition.rowIndex >= 0) {
                scrollToRow(newPosition.rowIndex);
              }

              requestAnimationFrame(() => {
                const cellElement = document.querySelector(
                  getCellSelector(newPosition.rowIndex, newPosition.columnId),
                );
                if (cellElement instanceof HTMLElement) {
                  cellElement.focus();
                  ensureCellVisible(cellElement);

                  if (onAnnouncement) {
                    const column = columns[columns.length - 1];
                    let announcement = '';

                    if (newPosition.rowIndex === ROW_INDEX.CATEGORY) {
                      announcement = `Category header row, ${column?.meta?.assignmentName || column?.id || 'column'}`;
                    } else if (newPosition.rowIndex === ROW_INDEX.ASSIGNMENT) {
                      announcement = `Assignment header: ${column?.meta?.assignmentName || column?.id || 'column'}`;
                    } else if (newPosition.rowIndex === ROW_INDEX.AVERAGE) {
                      announcement = `Class average row, ${column?.meta?.assignmentName || column?.id || 'column'}`;
                    } else if (data) {
                      const row = tableRows[newPosition.rowIndex]?.original;
                      if (row && column) {
                        if (column.id === COLUMN_ID.NAME) {
                          announcement = `Student: ${row.student.name}`;
                        } else if (
                          column.id?.startsWith(COLUMN_ID.CATEGORY_TOTAL_PREFIX)
                        ) {
                          const categoryId = column.id.replace(
                            COLUMN_ID.CATEGORY_TOTAL_PREFIX,
                            '',
                          );
                          const totalText = formatCategoryTotal(
                            row.categoryTotals?.[categoryId],
                            { gradingType, showPercent },
                          );
                          announcement = `${row.student.name}, Category Total: ${totalText}`;
                        }
                      }
                    }

                    if (announcement) {
                      onAnnouncement(announcement);
                    }
                  }
                }
              });
            } else {
              moveFocus('left');
            }
          } else {
            // Tab: go forwards
            if (
              currentColIndex === columns.length - 1 &&
              cellForTab.rowIndex === tableRows.length - 1
            ) {
              // At last cell - let Tab leave the table (default behavior)
              setFocusedCell(null);
              return;
            } else if (
              currentColIndex === columns.length - 1 &&
              cellForTab.rowIndex < tableRows.length - 1
            ) {
              const newPosition = {
                rowIndex: cellForTab.rowIndex + 1,
                columnId: columns[0]?.id,
              };
              setFocusedCell(newPosition);
              onCellFocus?.(newPosition);

              // Only scroll virtualizer for data rows
              if (scrollToRow && newPosition.rowIndex >= 0) {
                scrollToRow(newPosition.rowIndex);
              }

              requestAnimationFrame(() => {
                const cellElement = document.querySelector(
                  getCellSelector(newPosition.rowIndex, newPosition.columnId),
                );
                if (cellElement instanceof HTMLElement) {
                  cellElement.focus();
                  ensureCellVisible(cellElement);

                  if (onAnnouncement) {
                    const column = columns[0];
                    let announcement = '';

                    if (newPosition.rowIndex === ROW_INDEX.CATEGORY) {
                      announcement = `Category header row, ${column?.id || 'column'}`;
                    } else if (newPosition.rowIndex === ROW_INDEX.ASSIGNMENT) {
                      announcement = `Assignment header: ${column?.id || 'column'}`;
                    } else if (newPosition.rowIndex === ROW_INDEX.AVERAGE) {
                      announcement = `Class average row, ${column?.id || 'column'}`;
                    } else if (data) {
                      const row = tableRows[newPosition.rowIndex]?.original;
                      if (row && column) {
                        announcement = `Student: ${row.student.name}`;
                      }
                    }

                    if (announcement) {
                      onAnnouncement(announcement);
                    }
                  }
                }
              });
            } else {
              moveFocus('right');
            }
          }
          e.preventDefault();
          break;
        }
        case 'Enter':
        case ' ': {
          // Use focusedCell state, or fall back to activeElement's data attributes
          let cellToActivate = focusedCell;
          if (
            !cellToActivate &&
            document.activeElement instanceof HTMLElement
          ) {
            // First try the activeElement itself
            let { rowIndex, columnId } = document.activeElement.dataset;

            // If activeElement doesn't have row/column info, look for parent cell
            if (rowIndex === undefined || !columnId) {
              const parentCell = document.activeElement.closest(
                '[data-row-index][data-column-id]',
              );
              if (parentCell instanceof HTMLElement) {
                rowIndex = parentCell.dataset.rowIndex;
                columnId = parentCell.dataset.columnId;
              }
            }

            if (rowIndex !== undefined && columnId) {
              cellToActivate = {
                rowIndex: parseInt(rowIndex, 10),
                columnId,
              };
            }
          }

          if (cellToActivate) {
            // For cells with multiple interactive elements, don't auto-activate.
            // The user must Tab into the cell and activate a specific element.
            const cellEl = document.querySelector(
              getCellSelector(cellToActivate.rowIndex, cellToActivate.columnId),
            );
            const cellFocusableElements = getFocusableElements(cellEl);

            if (cellFocusableElements.length > 1) {
              e.preventDefault();
              break;
            }

            // For data rows, Enter/Space activates the cell (opens modal)
            if (cellToActivate.rowIndex >= 0) {
              // Don't set isEditing - we're opening a modal, not inline editing
              onCellEdit?.(cellToActivate);
            } else {
              // For header rows, trigger the cell activation callback
              onCellActivate?.(cellToActivate);
            }
          }
          e.preventDefault();
          break;
        }
        case 'Home':
          if (e.ctrlKey) {
            // Ctrl+Home goes to the very first cell (top-left of category row)
            const firstPosition = {
              rowIndex: MIN_ROW_INDEX,
              columnId: columns[0]?.id,
            };
            setFocusedCell(firstPosition);
            const firstCell = document.querySelector(
              getCellSelector(MIN_ROW_INDEX, columns[0]?.id),
            );
            if (firstCell instanceof HTMLElement) {
              firstCell.focus();
              ensureCellVisible(firstCell);
            }
          } else {
            if (focusedCell) {
              const newPosition = { ...focusedCell, columnId: columns[0]?.id };
              setFocusedCell(newPosition);
              const cell = document.querySelector(
                getCellSelector(focusedCell.rowIndex, columns[0]?.id),
              );
              if (cell instanceof HTMLElement) {
                cell.focus();
                ensureCellVisible(cell);
              }
            }
          }
          e.preventDefault();
          break;
        case 'End':
          if (e.ctrlKey) {
            const lastPosition = {
              rowIndex: tableRows.length - 1,
              columnId: columns[columns.length - 1]?.id,
            };
            setFocusedCell(lastPosition);
            const lastCell = document.querySelector(
              getCellSelector(
                tableRows.length - 1,
                columns[columns.length - 1]?.id,
              ),
            );
            if (lastCell instanceof HTMLElement) {
              lastCell.focus();
              ensureCellVisible(lastCell);
            }
          } else {
            if (focusedCell) {
              const newPosition = {
                ...focusedCell,
                columnId: columns[columns.length - 1]?.id,
              };
              setFocusedCell(newPosition);
              const cell = document.querySelector(
                getCellSelector(
                  focusedCell.rowIndex,
                  columns[columns.length - 1]?.id,
                ),
              );
              if (cell instanceof HTMLElement) {
                cell.focus();
                ensureCellVisible(cell);
              }
            }
          }
          e.preventDefault();
          break;
        default:
          break;
      }
    },
    [
      isEditing,
      isInsideCell,
      focusedCell,
      moveFocus,
      columns,
      tableRows,
      onCellEdit,
      onCellActivate,
      ensureCellVisible,
      onAnnouncement,
      data,
      onCellFocus,
      scrollToRow,
      focusCell,
      getFocusableElements,
      gradingType,
      showPercent,
    ],
  );

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  // Auto-focus single element in cells: when a cell receives focus and has
  // exactly one focusable element, redirect focus to that element. This makes
  // Tab flow directly to the interactive element without an extra Tab step.
  useEffect(() => {
    const handleFocusIn = (e) => {
      const target = e.target;
      if (!(target instanceof HTMLElement)) return;

      // Check if the focused element is a cell (has data-row-index attribute)
      if (target.dataset.rowIndex === undefined) return;

      // Get focusable elements inside the cell
      const focusableElements = Array.from(
        target.querySelectorAll(FOCUSABLE_SELECTOR),
      );

      // If exactly one focusable element, focus it instead of the cell
      if (focusableElements.length === 1) {
        const singleElement = focusableElements[0];
        if (singleElement instanceof HTMLElement) {
          // Track the cell we're in for keyboard navigation
          const rowIndex = parseInt(target.dataset.rowIndex, 10);
          const columnId = target.dataset.columnId;
          if (columnId) {
            setFocusedCell({ rowIndex, columnId });
            // For header rows (rowIndex < 0), set isInsideCell so Tab can reach the sort button
            // For data rows (grade cells), don't set isInsideCell - Tab moves to next cell,
            // Enter/Space triggers the action directly via onCellEdit
            if (rowIndex < 0) {
              setIsInsideCell(true);
            }
          }
          // Use setTimeout to allow the current focus event to complete
          setTimeout(() => singleElement.focus(), 0);
        }
      }
    };

    document.addEventListener('focusin', handleFocusIn);
    return () => document.removeEventListener('focusin', handleFocusIn);
  }, [setFocusedCell]);

  return {
    focusedCell,
    setFocusedCell,
    isEditing,
    setIsEditing,
    isInsideCell,
    moveFocus,
  };
}
